import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Download, Mail, Phone, MapPin, Building2, CheckCircle2, Printer, ArrowLeft, ZoomIn, ZoomOut, FileText, File, Eye, ExternalLink } from 'lucide-react';
import { formatCurrency } from '@/lib/app-utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getFileFromIDB } from '@/lib/idb-storage';
import { projectsAPI } from '@/services/projects';
import { quotationsAPI } from '@/services/quotations';

const PublicQuotationPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1);
  
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [filePreviewType, setFilePreviewType] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        let decoded;
        try {
          decoded = JSON.parse(atob(token));
        } catch (e) {
          throw new Error("Invalid access token");
        }

        const { p: projectId, q: quotationId } = decoded;

        if (!projectId || !quotationId) throw new Error("Invalid token data");

        await new Promise(resolve => setTimeout(resolve, 800));

        const projectRes = await projectsAPI.details(projectId);
        const quotationsRes = await quotationsAPI.list(projectId);

        if (!projectRes.success) throw new Error("Project not found");
        if (!quotationsRes.success) throw new Error("Quotations not found");

        const project = {
          id: projectRes.data.id,
          projectName: projectRes.data.project_name || projectRes.data.projectName,
          companyName: projectRes.data.company_name || projectRes.data.companyName,
          contactEmail: projectRes.data.contact_email || projectRes.data.contactEmail,
          contactPhone: projectRes.data.contact_phone || projectRes.data.contactPhone,
          contactName: projectRes.data.contact_name || projectRes.data.contactName,
          location: projectRes.data.project_location || projectRes.data.location,
          serialNumber: projectRes.data.serial_number || projectRes.data.serialNumber
        };

        const foundQuotation = quotationsRes.data.find(q => q.id === quotationId);
        if (!foundQuotation) throw new Error("Quotation not found or expired");

        const quotation = {
          id: foundQuotation.id,
          quotationNumber: foundQuotation.quotation_number || foundQuotation.quotationNumber,
          description: foundQuotation.description,
          unitType: foundQuotation.unit_type || foundQuotation.unitType,
          price: parseFloat(foundQuotation.price),
          quantity: parseFloat(foundQuotation.quantity),
          scrapValue: parseFloat(foundQuotation.scrap_value || foundQuotation.scrapValue || 0),
          total: parseFloat(foundQuotation.total),
          netValue: parseFloat(foundQuotation.net_value || foundQuotation.netValue),
          createdAt: foundQuotation.created_at || foundQuotation.createdAt,
          version: foundQuotation.version,
          versionType: foundQuotation.version_type || foundQuotation.versionType,
          fileId: foundQuotation.file_id || foundQuotation.fileId,
          pdfFileName: foundQuotation.pdf_file_name || foundQuotation.pdfFileName,
          fileSize: foundQuotation.file_size || foundQuotation.fileSize,
          fileUrl: foundQuotation.file_url || foundQuotation.fileUrl, // ✅ NEW
          pdfFile: foundQuotation.pdfFile
        };
        
        if (project && quotation) {
          setData({ project, quotation });
          await loadFilePreview(quotation);
        } else {
          throw new Error("Quotation not found or expired");
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token]);

  // ✅ تحديث loadFilePreview لدعم fileUrl
  const loadFilePreview = async (quotation) => {
    try {
      // ✅ Priority 1: استخدم fileUrl من السيرفر (الملفات الجديدة)
      if (quotation.fileUrl) {
        setFilePreviewUrl(quotation.fileUrl);
        
        // تحديد نوع الملف من الامتداد
        const ext = quotation.pdfFileName?.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') {
          setFilePreviewType('application/pdf');
        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
          setFilePreviewType(`image/${ext}`);
        } else {
          setFilePreviewType('application/octet-stream');
        }
        return;
      }

      // Priority 2: IndexedDB (الملفات القديمة)
      if (quotation.fileId) {
        const fileRecord = await getFileFromIDB(quotation.fileId);
        if (fileRecord && fileRecord.file) {
          const url = URL.createObjectURL(fileRecord.file);
          setFilePreviewUrl(url);
          setFilePreviewType(fileRecord.file.type);
          return;
        }
      }

      // Priority 3: Base64 (الملفات القديمة جداً)
      if (quotation.pdfFile) {
        const arr = quotation.pdfFile.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        const blob = new Blob([u8arr], { type: mime });
        const url = URL.createObjectURL(blob);
        setFilePreviewUrl(url);
        setFilePreviewType(mime);
      }
    } catch (e) {
      console.error("Error loading file preview:", e);
    }
  };

  useEffect(() => {
    return () => {
      // لا تحذف الـ URL إذا كان من السيرفر
      if (filePreviewUrl && !data?.quotation?.fileUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl, data]);

  // ✅ تحديث handleDownloadPdf
  const handleDownloadPdf = async () => {
    const { quotation } = data;
    
    // ✅ Priority 1: تنزيل من السيرفر مباشرة
    if (quotation.fileUrl) {
      try {
        const link = document.createElement('a');
        link.href = quotation.fileUrl;
        link.download = quotation.pdfFileName || `Quotation_${quotation.quotationNumber}.pdf`;
        link.target = '_blank'; // فتح في تاب جديد إذا التنزيل فشل
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      } catch (e) {
        console.error("Download from server failed:", e);
      }
    }
    
    // Priority 2: IndexedDB
    if (quotation.fileId) {
       try {
          const fileRecord = await getFileFromIDB(quotation.fileId);
          if (fileRecord && fileRecord.file) {
             const url = URL.createObjectURL(fileRecord.file);
             const link = document.createElement('a');
             link.href = url;
             link.download = quotation.pdfFileName || `Quotation_${quotation.quotationNumber}.pdf`;
             document.body.appendChild(link);
             link.click();
             document.body.removeChild(link);
             URL.revokeObjectURL(url);
             return;
          }
       } catch (e) {
        console.error("Error retrieving file:", e);
       }
    }

    // Priority 3: Base64
    if (quotation.pdfFile) {
       try {
        const arr = quotation.pdfFile.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        const blob = new Blob([u8arr], { type: mime });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = quotation.pdfFileName || `Quotation_${quotation.quotationNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return;
       } catch (e) {
         console.error("Download failed", e);
       }
    }
    
    toast({ 
      title: "No PDF Attached", 
      description: "Opening print dialog. You can save as PDF from there.",
    });
    window.print();
  };

  const handleViewInNewTab = () => {
    if (filePreviewUrl) {
      window.open(filePreviewUrl, '_blank');
    }
  };

  // ✅ تحديث handlePrint
  const handlePrint = async () => {
    const { quotation } = data;
    
    // ✅ إذا في fileUrl، استخدمه مباشرة
    if (quotation.fileUrl) {
      window.open(quotation.fileUrl, '_blank');
      return;
    }
    
    let pdfBlob = null;

    if (quotation.fileId) {
      try {
        const fileRecord = await getFileFromIDB(quotation.fileId);
        if (fileRecord && fileRecord.file) {
          pdfBlob = fileRecord.file;
        }
      } catch (e) {
        console.error("Error retrieving file:", e);
      }
    }

    if (!pdfBlob && quotation.pdfFile) {
      try {
        const arr = quotation.pdfFile.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        pdfBlob = new Blob([u8arr], { type: mime });
      } catch (e) {
        console.error("Error converting base64 to blob:", e);
      }
    }

    if (pdfBlob) {
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;
      
      iframe.onload = function() {
        try {
          this.contentWindow.focus();
          this.contentWindow.print();
        } catch (e) {
          console.error("Print blocked:", e);
          toast({ 
            title: "Print Blocked", 
            description: "Please allow popups for this site to print PDF.",
            variant: "destructive"
          });
        }
        
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(pdfUrl);
        }, 2000);
      };
      
      document.body.appendChild(iframe);
    } else {
      toast({ 
        title: "No PDF Attached", 
        description: "Opening page print dialog instead.",
      });
      window.print();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center animate-in fade-in duration-700">
           <img 
             src="https://horizons-cdn.hostinger.com/36b8ba49-dacf-43e0-a590-4c4a8f30ca5d/39448dacd86945f67d0edd1a1f9271f1.png" 
             alt="Stone Beam Logo" 
             className="h-16 mb-8 animate-pulse"
           />
           <div className="h-1 w-48 bg-gray-200 rounded overflow-hidden">
              <div className="h-full bg-safety-orange animate-[progress_1s_ease-in-out_infinite]" style={{width: '30%'}}></div>
           </div>
           <p className="mt-4 text-sm text-stone-grey font-medium tracking-wide">LOADING QUOTATION...</p>
        </div>
        <style>{`
          @keyframes progress {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(300%); }
          }
        `}</style>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md w-full border border-red-100">
         <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
           <FileText className="w-8 h-8 text-red-500" />
         </div>
         <h1 className="text-2xl font-bold text-navy-steal mb-2">Access Denied</h1>
         <p className="text-stone-grey mb-6">{error || "This link is invalid or has expired."}</p>
         <Button onClick={() => window.location.href = '/login'} className="bg-navy-steal hover:bg-charcoal w-full">
           Return to Portal
         </Button>
        </div>
      </div>
    );
  }

  const { project, quotation } = data;

  const subtotal = quotation.total;
  const vatRate = 0.05;
  const vatAmount = subtotal * vatRate;
  const totalWithVat = subtotal + vatAmount;

  return (
    <>
      <Helmet>
        <title>Quotation {quotation.quotationNumber} - Stone Beam Demolition</title>
        <meta name="description" content={`Quotation for ${project.projectName} by Stone Beam Demolition`} />
      </Helmet>

      <div className="min-h-screen bg-slate-100 pb-12 print:bg-white print:pb-0 font-sans">
        
        {/* HERO BANNER */}
        <div className="relative h-[300px] lg:h-[350px] w-full bg-navy-steal overflow-hidden print:hidden">
          <div className="absolute inset-0 z-0">
             <img 
               src="https://horizons-cdn.hostinger.com/36b8ba49-dacf-43e0-a590-4c4a8f30ca5d/a036dcb55758f10d68135fb36b2ea0f9.png" 
               alt="Demolition Site" 
               className="w-full h-full object-cover opacity-60"
             />
             <div className="absolute inset-0 bg-gradient-to-b from-navy-steal/80 to-navy-steal/90" />
          </div>
          
          <div className="relative z-10 container max-w-7xl mx-auto px-4 h-full flex flex-col items-center justify-center text-center">
            <img 
               src="https://horizons-cdn.hostinger.com/36b8ba49-dacf-43e0-a590-4c4a8f30ca5d/39448dacd86945f67d0edd1a1f9271f1.png" 
               alt="Stone Beam" 
               className="h-16 md:h-24 mb-4 drop-shadow-xl"
            />
            <div className="h-1 w-20 bg-safety-orange rounded-full mb-4"></div>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight uppercase">Demolition Services</h1>
            <p className="text-stone-300 mt-2 text-sm md:text-base tracking-widest uppercase font-medium">Excellence in every project</p>
          </div>
        </div>

        <div className="container max-w-7xl mx-auto px-4 -mt-16 relative z-20 print:mt-0 print:max-w-full print:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            
            {/* LEFT COLUMN */}
            <div className="lg:col-span-8 flex flex-col gap-6">
              
              {/* Controls Bar */}
              <div className="bg-white rounded-xl shadow-md p-3 flex flex-wrap gap-2 justify-between items-center print:hidden border border-slate-200/60">
                 <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setScale(s => Math.max(0.5, s - 0.1))}><ZoomOut className="w-4 h-4" /></Button>
                    <span className="text-xs font-mono text-stone-500 w-12 text-center">{Math.round(scale * 100)}%</span>
                    <Button variant="ghost" size="sm" onClick={() => setScale(s => Math.min(2, s + 0.1))}><ZoomIn className="w-4 h-4" /></Button>
                 </div>
                 <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
                       <Printer className="w-4 h-4" /> Print
                    </Button>
                    <Button onClick={handleDownloadPdf} className="bg-safety-orange hover:bg-orange-700 text-white gap-2 shadow-sm shadow-orange-200">
                       <Download className="w-4 h-4" /> Download
                    </Button>
                 </div>
              </div>

              {/* Quotation Paper - نفس الكود القديم */}
              <div 
                className="bg-white rounded-xl shadow-xl overflow-hidden print:shadow-none print:rounded-none print:overflow-visible transition-transform duration-200 origin-top"
                style={{ transform: `scale(${scale})`, marginBottom: `${(scale - 1) * 100}%` }}
              >
                {/* ... باقي كود الـ Quotation Paper نفسه ... */}
                <div className="hidden print:block p-8 border-b-2 border-navy-steal mb-4">
                   <div className="flex justify-between items-end">
                      <img src="https://horizons-cdn.hostinger.com/36b8ba49-dacf-43e0-a590-4c4a8f30ca5d/39448dacd86945f67d0edd1a1f9271f1.png" alt="Logo" className="h-16" />
                      <div className="text-right text-sm">
                         <div className="font-bold text-navy-steal">Stone Beam Demolition</div>
                         <div>+971 55 930 8594</div>
                         <div>info@sbdemolition.ae</div>
                         <div>Dubai, UAE</div>
                      </div>
                   </div>
                </div>

                <div className="p-8 md:p-12 print:p-0">
                  <div className="flex flex-col md:flex-row justify-between items-start border-b border-slate-100 pb-8 mb-8">
                     <div>
                        <div className="flex items-center gap-3 mb-3">
                           <span className="bg-navy-steal text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                              Quotation
                           </span>
                           <span className="bg-orange-100 text-orange-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider border border-orange-200">
                              {quotation.versionType?.toUpperCase() || 'ORIGINAL'}
                           </span>
                        </div>
                        <h2 className="text-4xl font-bold text-navy-steal mb-1">{quotation.quotationNumber}</h2>
                        <div className="text-stone-400 text-sm font-mono">Ref: {project.serialNumber ? `PRJ-${project.serialNumber.toString().padStart(3,'0')}` : 'PRJ-NEW'}</div>
                     </div>
                     <div className="mt-4 md:mt-0 md:text-right bg-slate-50 p-4 rounded-lg border border-slate-100 print:bg-transparent print:border-none print:p-0">
                        <div className="text-xs text-stone-500 uppercase tracking-wider font-semibold mb-1">Date Created</div>
                        <div className="text-lg font-bold text-charcoal">
                           {new Date(quotation.createdAt).toLocaleDateString('en-AE', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                        <div className="text-xs text-stone-400 mt-1">Valid for 30 days</div>
                     </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 mb-12">
                     <div>
                        <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">Issued To</h3>
                        <div className="text-lg font-bold text-navy-steal mb-1">{project.companyName}</div>
                        <div className="text-stone-600 text-sm">
                           Attn: {project.contactName || 'Procurement Manager'}<br />
                           {project.contactEmail && <span className="text-blue-600">{project.contactEmail}</span>}
                        </div>
                     </div>
                     <div>
                        <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">Project</h3>
                        <div className="text-lg font-bold text-navy-steal mb-1">{project.projectName}</div>
                        <div className="text-stone-600 text-sm max-w-xs">
                           {project.location || 'Dubai, UAE'}
                        </div>
                     </div>
                  </div>

                  <div className="border rounded-lg overflow-hidden mb-8 print:border-black">
                     <Table>
                       <TableHeader className="bg-slate-50 print:bg-gray-100 print:text-black">
                         <TableRow className="print:border-black">
                           <TableHead className="font-bold text-navy-steal print:text-black">Description</TableHead>
                           <TableHead className="font-bold text-navy-steal w-[100px] print:text-black">Type</TableHead>
                           <TableHead className="font-bold text-navy-steal text-right w-[120px] print:text-black">Price</TableHead>
                           <TableHead className="font-bold text-navy-steal text-right w-[80px] print:text-black">Qty</TableHead>
                           <TableHead className="font-bold text-navy-steal text-right w-[120px] print:text-black">Total</TableHead>
                         </TableRow>
                       </TableHeader>
                       <TableBody>
                         <TableRow className="print:border-black">
                           <TableCell className="py-6 align-top">
                              <div className="font-medium text-charcoal text-base mb-1">{quotation.description}</div>
                              <div className="text-stone-500 text-xs leading-relaxed max-w-lg">
                                 Includes all labor, machinery, and disposal fees as per standard demolition regulations.
                              </div>
                           </TableCell>
                           <TableCell className="py-6 align-top text-stone-600 font-medium text-xs uppercase">{quotation.unitType}</TableCell>
                           <TableCell className="py-6 align-top text-right font-mono text-stone-600">{formatCurrency(quotation.price)}</TableCell>
                           <TableCell className="py-6 align-top text-right font-mono text-stone-600">{quotation.unitType === 'Unit Rate' ? quotation.quantity : '1'}</TableCell>
                           <TableCell className="py-6 align-top text-right font-bold text-navy-steal font-mono">{formatCurrency(quotation.total)}</TableCell>
                         </TableRow>
                       </TableBody>
                     </Table>
                  </div>

                  <div className="flex justify-end mb-12">
                     <div className="w-full md:w-1/2 lg:w-1/3 bg-slate-50 rounded-lg p-6 border border-slate-100 print:bg-transparent print:border-none">
                        <div className="flex justify-between items-center mb-3 text-sm">
                           <span className="text-stone-600">Subtotal</span>
                           <span className="font-medium">{formatCurrency(subtotal)}</span>
                        </div>
                        <div className="flex justify-between items-center mb-4 text-sm">
                           <span className="text-stone-600">VAT (5%)</span>
                           <span className="font-medium">{formatCurrency(vatAmount)}</span>
                        </div>
                        <div className="h-px bg-slate-200 mb-4 print:bg-black"></div>
                        <div className="flex justify-between items-center">
                           <span className="text-base font-bold text-navy-steal uppercase">Total Amount</span>
                           <span className="text-xl font-black text-navy-steal">{formatCurrency(totalWithVat)}</span>
                        </div>
                     </div>
                  </div>

                  <div className="text-xs text-stone-400 pt-8 border-t border-slate-100 print:text-black print:border-black">
                     <p className="mb-2 font-bold uppercase">Terms & Conditions:</p>
                     <ul className="list-disc pl-4 space-y-1 mb-6">
                        <li>This quotation is valid for 30 days from the date of issue.</li>
                        <li>Payment terms are as per the agreed contract.</li>
                        <li>All work will be carried out in accordance with UAE safety regulations.</li>
                     </ul>
                     <p className="text-center mt-12 print:block hidden">Thank you for your business!</p>
                  </div>

                </div>
              </div>

              {/* ✅ FILE PREVIEW */}
              {filePreviewUrl && (
                <div className="bg-white rounded-xl shadow-xl overflow-hidden print:hidden">
                  <div className="bg-navy-steal text-white p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <File className="w-5 h-5 text-safety-orange" />
                      <div>
                        <h3 className="font-bold text-sm">Attached Document</h3>
                        <p className="text-xs text-stone-300">{quotation.pdfFileName || 'Document'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {isMobile && filePreviewType?.includes('pdf') && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={handleViewInNewTab}
                          className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        onClick={handleDownloadPdf}
                        className="bg-safety-orange hover:bg-orange-700 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-50">
                    {!isMobile ? (
                      <>
                        {filePreviewType?.includes('pdf') ? (
                          <iframe
                            src={filePreviewUrl}
                            className="w-full h-[800px] border border-slate-200 rounded-lg bg-white"
                            title="PDF Preview"
                          />
                        ) : filePreviewType?.startsWith('image/') ? (
                          <img 
                            src={filePreviewUrl} 
                            alt="Attached document"
                            className="w-full max-h-[800px] object-contain border border-slate-200 rounded-lg bg-white"
                          />
                        ) : (
                          <div className="w-full h-[600px] border border-slate-200 rounded-lg bg-white flex flex-col items-center justify-center">
                            <File className="w-16 h-16 text-slate-300 mb-4" />
                            <p className="text-slate-600 font-medium mb-2">Preview not available for this file type</p>
                            <p className="text-sm text-slate-400 mb-4">Click the download button to view the file</p>
                            <Button 
                              onClick={handleDownloadPdf}
                              className="bg-navy-steal hover:bg-charcoal text-white"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download File
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full border-2 border-dashed border-slate-300 rounded-lg bg-white p-8 flex flex-col items-center justify-center text-center">
                        <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                          <File className="w-8 h-8 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-bold text-navy-steal mb-2">
                          {quotation.pdfFileName || 'Attached Document'}
                        </h3>
                        <p className="text-sm text-stone-500 mb-6 max-w-sm">
                          PDF preview is not supported on mobile browsers. Tap the button below to download or open in a new tab.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                          {filePreviewType?.includes('pdf') && (
                            <Button 
                              onClick={handleViewInNewTab}
                              variant="outline"
                              className="flex-1"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Open in Browser
                            </Button>
                          )}
                          <Button 
                            onClick={handleDownloadPdf}
                            className="flex-1 bg-safety-orange hover:bg-orange-700 text-white"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download File
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* RIGHT COLUMN */}
            <div className="lg:col-span-4 space-y-6 print:hidden">
                
               <Card className="shadow-lg border-t-4 border-t-safety-orange hover:shadow-xl transition-shadow">
                <CardHeader className="pb-3">
                   <CardTitle className="flex items-center gap-2 text-lg">
                      <Building2 className="w-5 h-5 text-safety-orange" />
                      Why Stone Beam?
                   </CardTitle>
                </CardHeader>
                <CardContent>
                   <ul className="space-y-4">
                      {[
                         "Engineering-Driven Demolition, Not Just Breaking",
                         "First in the Region with 3D Simulation-Led Planning",
                         "Full Spectrum of Advanced Demolition Methods",
                         "Proven Safety & Strong HSE Culture",
                         "Trusted by Dubai Authorities & Leading Developers",
                         "Built Around Dubai's Future-Ready Standards"
                      ].map((point, i) => (
                         <li key={i} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                            <span className="text-sm text-stone-700 font-medium leading-tight">{point}</span>
                         </li>
                      ))}
                   </ul>
                </CardContent>
               </Card>

               <Card className="shadow-lg bg-navy-steal text-white border-none">
                <CardHeader className="pb-3">
                   <CardTitle className="text-lg text-white">Need Assistance?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                   <div className="flex items-start gap-4 group">
                      <div className="p-2.5 bg-white/10 rounded-lg group-hover:bg-safety-orange transition-colors">
                         <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                         <p className="text-xs text-stone-300 uppercase tracking-wider font-bold mb-0.5">Email Us</p>
                         <a href="mailto:info@sbdemolition.ae" className="text-sm font-semibold hover:text-safety-orange transition-colors">info@sbdemolition.ae</a>
                      </div>
                   </div>
                   
                   <div className="flex items-start gap-4 group">
                      <div className="p-2.5 bg-white/10 rounded-lg group-hover:bg-safety-orange transition-colors">
                         <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                         <p className="text-xs text-stone-300 uppercase tracking-wider font-bold mb-0.5">Call Us</p>
                         <a href="tel:+971559308594" className="text-sm font-semibold hover:text-safety-orange transition-colors">+971 55 930 8594</a>
                      </div>
                   </div>

                   <div className="flex items-start gap-4 group">
                      <div className="p-2.5 bg-white/10 rounded-lg group-hover:bg-safety-orange transition-colors">
                         <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                         <p className="text-xs text-stone-300 uppercase tracking-wider font-bold mb-0.5">Location</p>
                         <p className="text-sm font-semibold">office 2601, Marina Plaza, Dubai, UAE</p>
                      </div>
                   </div>
                </CardContent>
               </Card>

               <div className="text-center pt-4">
                  <a href="/login" className="text-xs text-stone-400 hover:text-navy-steal flex items-center justify-center gap-1 transition-colors">
                     <ArrowLeft className="w-3 h-3" /> Staff Portal Login
                  </a>
               </div>

            </div>
          </div>

          <footer className="mt-16 mb-8 text-center print:hidden">
             <div className="h-px w-24 bg-gray-300 mx-auto mb-6"></div>
             <p className="text-sm text-stone-500 font-medium">© 2026 Stone Beam Demolition. All rights reserved.</p>
             <p className="text-xs text-stone-400 mt-2">Engineered for Excellence.</p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default PublicQuotationPage;
